import { getAuthToken } from './auth/tokenStorage';

import { API_URL } from '../config';

export interface BankingInfo {
  id: string;
  bankName: string;
  accountType: string;
  accountNumber?: string;
  routingNumber?: string;
  branchAddress?: string;
  branchPhone?: string;
  onlineBankingUrl?: string;
  onlineBankingUsername?: string;
  onlineBankingPassword?: string;
  username?: string;
  password?: string;
  contactPerson?: string;
  contactPhone?: string;
  accountBalance?: string;
  notes?: string;
  isPrimary?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateBankingDto {
  bankName: string;
  accountType: string;
  accountNumber?: string;
  routingNumber?: string;
  branchAddress?: string;
  branchPhone?: string;
  onlineBankingUrl?: string;
  onlineBankingUsername?: string;
  onlineBankingPassword?: string;
  contactPerson?: string;
  contactPhone?: string;
  accountBalance?: string;
  notes?: string;
  isPrimary?: boolean;
}

class BankingInfoService {
  private async getAuthToken(): Promise<string | null> {
    return await getAuthToken();
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    });

    return response;
  }

  async createBankingInfo(data: CreateBankingDto): Promise<BankingInfo> {
    const response = await this.makeRequest('/api/banking-info', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to save banking information');
    }

    const result = await response.json();
    return result.data;
  }

  async getAllBankingInfo(targetUserId?: string): Promise<BankingInfo[]> {
    const url = targetUserId
      ? `/api/banking-info?targetUserId=${targetUserId}`
      : '/api/banking-info';

    const response = await this.makeRequest(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch banking information');
    }

    const result = await response.json();
    return result.data || [];
  }

  async getBankingInfo(id: string): Promise<BankingInfo> {
    const response = await this.makeRequest(`/api/banking-info/${id}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch banking information');
    }

    const result = await response.json();
    return result.data;
  }

  async updateBankingInfo(id: string, data: Partial<CreateBankingDto>): Promise<BankingInfo> {
    const response = await this.makeRequest(`/api/banking-info/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to update banking information');
    }

    const result = await response.json();
    return result.data;
  }

  async deleteBankingInfo(id: string): Promise<void> {
    const response = await this.makeRequest(`/api/banking-info/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to delete banking information');
    }
  }

  async deleteAllBankingInfo(): Promise<void> {
    const response = await this.makeRequest('/api/banking-info', {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to delete all banking information');
    }
  }

  // Lock/Unlock methods using unified access grants
  async lockBankingInfo(id: string): Promise<void> {
    const response = await this.makeRequest(`/api/banking-info/${id}/lock`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to lock banking information');
    }
  }

  async unlockBankingInfo(id: string, granteeEmails: string[]): Promise<void> {
    const response = await this.makeRequest(`/api/banking-info/${id}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ granteeEmails }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to unlock banking information');
    }
  }

  async getBankingAccessGrants(id: string): Promise<any[]> {
    const response = await this.makeRequest(`/api/banking-info/${id}/access-grants`, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch access grants');
    }

    const result = await response.json();
    return result.data || [];
  }
}

export const bankingInfoService = new BankingInfoService();
